import React, { useEffect, useContext, useState } from "react";
import { SketchfabContext } from "../../Sketchfab/SketchfabContext";
import { ConfigurationContext } from "./ConfigurationContext";
import modelNodes from "./modelNodes";
import modelMaterials from "./modelMaterials";

const RenderIframe = ({ viewerRef }) => {
  const { api, ready, mat } = useContext(SketchfabContext);
  const {
    size,
    heaters: { heater, upgrades },
    interiorexterior: { liner, seating, lidCover, wood, linerColor },
    accessories: { flueDrain, wooden, electrical },
  } = useContext(ConfigurationContext);

  const [sizeNodes, setSizeNodes] = useState(modelNodes["1.6m"].nodes);
  const [heaterNodes, setHeaterNodes] = useState([]);
  const [upgradeNodes, setUpgradeNodes] = useState([]);
  const [linerNodes, setLinerNodes] = useState([]);
  const [seatingNodes, setSeatingNodes] = useState([]);
  const [lidCoverNodes, setLidCoverNodes] = useState([]);
  const [flueDrainNodes, setFlueDrainNodes] = useState([]);
  const [electricalNodes, setElectricalNodes] = useState([]);
  const [woodenNodes, setWoodenNodes] = useState([]);

  useEffect(() => {
    if (ready && size) {
      sizeNodes.forEach((node) => api.hide(node));

      const nodes = modelNodes[size?.label ?? "1.6m"].nodes;

      nodes.forEach((node) => api.show(node));

      setSizeNodes(nodes);
    }
  }, [ready, size]);

  useEffect(() => {
    if (ready) {
      const isConnections =
        upgrades.filter((item) => item.label === "90-Degree Heater Connections")
          .length > 0;

      const nodes =
        heater?.label === "Internal"
          ? modelNodes[size.label].heater[heater.label].nodes
          : heater?.label === "External"
          ? isConnections
            ? modelNodes[size.label].heater[heater.label][heater.variant]
                .upgrades["90-Degree Heater Connections"].nodes
            : modelNodes[size.label].heater[heater.label][heater.variant].nodes
          : [];

      heaterNodes.forEach((node) => api.hide(node));
      nodes.forEach((node) => api.show(node));

      setHeaterNodes(nodes);
    }
  }, [ready, heater, upgrades, size]);

  useEffect(() => {
    if (ready) {
      const isConnections =
        upgrades.filter((item) => item.label === "90-Degree Heater Connections")
          .length > 0;

      const start =
        heater?.label === "External"
          ? isConnections
            ? modelNodes[size.label].heater["External"][heater.variant]
                .upgrades["90-Degree Heater Connections"].upgrades[
                "External Heater Glass Door"
              ].initial
            : modelNodes[size.label].heater["External"][heater.variant]
                .upgrades["External Heater Glass Door"].initial
          : [];

      const nodes = upgrades.reduce((acc, upgrade) => {
        if (isConnections && upgrade.label === "External Heater Glass Door") {
          const filtered = acc.filter(
            (x) =>
              modelNodes[size.label].heater["External"][
                heater.variant
              ].upgrades["90-Degree Heater Connections"].upgrades[
                "External Heater Glass Door"
              ].initial.indexOf(x) < 0
          );
          return [
            ...filtered,
            ...modelNodes[size.label].heater["External"][heater.variant]
              .upgrades["90-Degree Heater Connections"].upgrades[
              "External Heater Glass Door"
            ].nodes,
          ];
        }

        if (upgrade.label === "External Heater Glass Door") {
          const filtered = acc.filter(
            (x) =>
              modelNodes[size.label].heater["External"][
                heater.variant
              ].upgrades["External Heater Glass Door"].initial.indexOf(x) < 0
          );
          return [
            ...filtered,
            ...modelNodes[size.label].heater["External"][heater.variant]
              .upgrades["External Heater Glass Door"].nodes,
          ];
        }
        if (upgrade.label === "Cover Around Internal Heater") {
          return [
            ...acc,
            ...modelNodes[size.label].heater["Internal"].upgrades[
              "Cover Around Internal Heater"
            ].nodes,
          ];
        }
        return acc;
      }, start);

      upgradeNodes.forEach((node) => api.hide(node));
      nodes.forEach((node) => api.show(node));
      setUpgradeNodes(nodes);
    }
  }, [ready, heater, upgrades, size]);

  useEffect(() => {
    if (ready) {
      linerNodes.forEach((node) => api.hide(node));
      const nodes = liner.reduce(
        (acc, item) =>
          item.label === "Add Polypropylene Liner"
            ? heater?.label === "External"
              ? [
                  ...acc,
                  ...modelNodes[size.label].heater[heater.label][heater.variant]
                    .interiorexterior.liner.nodes,
                ]
              : [...acc, ...modelNodes[size.label].interiorexterior.liner.nodes]
            : acc,
        []
      );
      nodes.forEach((node) => api.show(node));
      setLinerNodes(nodes);
    }
  }, [ready, liner, size, heater]);

  useEffect(() => {
    if (ready && size && seating) {
      seatingNodes.forEach((node) => api.hide(node));

      const nodes = seating?.label
        ? heater?.label === "Internal"
          ? modelNodes[size.label].heater[heater?.label].interiorexterior[
              seating?.label
            ].nodes
          : modelNodes[size.label].interiorexterior.seating[seating?.label]
              .nodes
        : modelNodes[size.label].interiorexterior.seating["Wooden Bench"].nodes;

      nodes.forEach((node) => api.show(node));
      setSeatingNodes(nodes);
    }
  }, [ready, seating, heater, size]);

  useEffect(() => {
    if (ready && lidCover) {
      lidCoverNodes.forEach((node) => api.hide(node));

      const nodes = lidCover?.label
        ? heater.label === "Internal" && lidCover.label === "Wooden"
          ? modelNodes[size.label].heater["Internal"].lidCover["Wooden"].nodes
          : modelNodes[size.label].interiorexterior.lidCover[lidCover?.label]
              .nodes
        : modelNodes[size.label].interiorexterior.seating["No Cover"].nodes;

      nodes.forEach((node) => api.show(node));
      setLidCoverNodes(nodes);
    } else {
      lidCoverNodes.forEach((node) => api.hide(node));
    }
  }, [ready, lidCover, heater, size]);

  useEffect(() => {
    if (ready) {
      const isConnections =
        upgrades.filter((item) => item.label === "90-Degree Heater Connections")
          .length > 0;

      flueDrainNodes.forEach((node) => api.hide(node));

      const nodes = flueDrain.reduce((acc, item) => {
        if (item.label === "High-Flow Drain Tap") {
          return [
            ...acc,
            ...modelNodes[size.label].accessories.flueDrain[
              "High-Flow Drain Tap"
            ].nodes,
          ];
        }
        if (
          item.label === "Chimney Heat Guard" &&
          heater?.label === "Internal"
        ) {
          return [
            ...acc,
            ...modelNodes[size.label].heater["Internal"].flueDrain[
              "Chimney Heat Guard"
            ].nodes,
          ];
        }
        if (
          item.label === "Chimney Heat Guard" &&
          heater?.label === "External"
        ) {
          const heatGuardNodes = isConnections
            ? modelNodes[size.label].heater["External"][heater.variant]
                .upgrades["90-Degree Heater Connections"].flueDrain[
                "Chimney Heat Guard"
              ].nodes
            : modelNodes[size.label].heater["External"][heater.variant]
                .flueDrain["Chimney Heat Guard"].nodes;
          return [...acc, ...heatGuardNodes];
        }
        return acc;
      }, []);

      nodes.forEach((node) => api.show(node));
      setFlueDrainNodes(nodes);
    }
  }, [ready, flueDrain, heater, upgrades, size]);

  useEffect(() => {
    if (ready && size) {
      const isStepsSelected =
        wooden.filter((item) => item.label === "Wide Bespoke Steps").length > 0;
      const initialStepNodes =
        modelNodes[size.label].accessories.wooden["Wide Bespoke Steps"].initial;
      const isLinerSelected = liner.length > 0;
      const initialLinerNodes = isLinerSelected
        ? modelNodes[size.label].interiorexterior.liner.nodes
        : [];

      const start = !isStepsSelected
        ? [...initialStepNodes, ...initialLinerNodes]
        : initialLinerNodes;

      [...initialStepNodes, ...initialLinerNodes, ...woodenNodes].forEach(
        (node) => api.hide(node)
      );

      const nodes = wooden.reduce((acc, item) => {
        if (
          heater.label === "Internal" &&
          item.label === "Mini Bar / Drink Holder"
        ) {
          return [
            ...acc,
            ...modelNodes[size.label].heater["Internal"].wooden[
              "Mini Bar / Drink Holder"
            ].nodes,
          ];
        }

        if (modelNodes[size.label].accessories.wooden[item.label]) {
          return [
            ...acc,
            ...modelNodes[size.label].accessories.wooden[item.label].nodes,
          ];
        }

        return acc;
      }, start);

      nodes.forEach((node) => api.show(node));
      setWoodenNodes(nodes);
    }
  }, [ready, wooden, liner, heater, size]);

  useEffect(() => {
    if (ready) {
      electricalNodes.forEach((node) => api.hide(node));

      const nodes = electrical.reduce((acc, item) => {
        if (
          item.label === "LED Underwater Lighting" ||
          item.label === "Hydro Massage System"
        ) {
          return [
            ...acc,
            ...modelNodes[size.label].accessories.electrical[item.label][
              item.variant
            ].nodes,
          ];
        }

        if (modelNodes[size.label].accessories.electrical[item.label]) {
          return [
            ...acc,
            ...modelNodes[size.label].accessories.electrical[item.label].nodes,
          ];
        }

        return acc;
      }, []);

      nodes.forEach((node) => api.show(node));
      setElectricalNodes(nodes);
    }
  }, [ready, electrical, size]);

  useEffect(() => {
    if (ready && mat) {
      wood
        ? mat.updateTexture("Wood-SPRUCE", modelMaterials.wood[wood.label])
        : mat.updateTexture("Wood-SPRUCE", modelMaterials.wood["Spruce"]);
    }
  }, [wood, ready, mat]);

  useEffect(() => {
    if (linerColor && ready && mat) {
      mat.updateTexture(
        "Polypropylene Liner blue",
        modelMaterials.linerColor[linerColor.label]
      );
    }
  }, [linerColor, ready, mat]);

  return (
    <iframe
      ref={viewerRef}
      title="sketchfab-viewer"
      style={{ height: "100%", width: "100%" }}
    />
  );
};

export default RenderIframe;
